import { iconNavAdicionar1, iconSetaDuplaEsquerda } from '@bsds-lib-component/icons/icons';
import { BsdsIcon, BsdsInput } from '@bsds-lib-component/react';
import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import {
  Button,
  SelfBillingTable,
  SubEstipulanteInfoSection,
  SubestipulantesListSection,
  SystemMessage,
  VidaTableListToolbar,
  Visibility,
} from '../../../../components';
import SeguradoInconsistencyModal from '../../../../components/pagesComponents/vidaPageComponents/modal/SeguradoInconsistencyModal';
import PersonFormActionButtons from '../../../../components/pagesComponents/vidaPageComponents/PersonFormActionButtons';
import {
  getSelfBillingErrorRoute,
  useSelfBilling,
  useSubEstipulanteInfo,
  useVidaTableListControls,
} from '../../../../hooks';
import { usePersonFormWizard } from '../../../../hooks/vida/usePersonFormWizard';
import { useSeguradoRelatedPanels } from '../../../../hooks/vida/useSeguradoRelatedPanels';
import { useSystemMessage } from '../../../../hooks/useSystemMessage';
import { MessageService } from '../../../../services/messageService';
import type { NewInsuredFormValues } from '../../../../types/vida/cadastralMaintenance/newInsuredFormTypes';
import type { PersonFormSection } from '../../../../types/vida/cadastralMaintenance/personFormTypes';
import type {
  SeguradoDataReturn,
  SeguradoDetailBeneficiaryItem,
} from '../../../../types/vida/maintenanceSegurado/maintenanceSeguradoType';
import type { ListaSubestipulanteItem } from '../../../../types/vida/subEstipulante/subEstipulanteType';
import type { SelfBillingTableItem } from '../../../../types/vida/table/selfBillingTableType';
import { extractBsdsInputValue } from '../../../../utils/bsdsInputUtils';
import {
  SEGURADOS_SORT_OPTIONS,
  sortSeguradoTableRows,
  filterSeguradoTableRows,
  EMPTY_SEGURADOS_PANEL_FILTERS,
  isSeguradoInconsistencyError,
  parseSeguradoInconsistenciesFromError,
} from '../../../../utils/selfBillingUtil';
import NewSeguradoForm from './NewSeguradoForm';
import SeguradoFormSection from './SeguradoFormSection';
import SeguradoRelatedPanelsSection from './SeguradoRelatedPanelsSection';

const TAB_LABEL = 'Auto-Faturamento';

const resolveSelfBillingDisplayError = (
  error: unknown,
  route: string,
  statusCode = 500,
): string => {
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  if (typeof error === 'string' && error.trim()) {
    return error;
  }
  return MessageService.getMappedErrorMessage(route, statusCode) ?? 'Erro na requisição';
};

const SelfBillingTab: React.FC = () => {
  const PAGE_SIZE = 10;
  const mainContentId = useId();
  const messageId = useId();
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddSegurado, setShowAddSegurado] = useState(false);
  const [selectedSegurado, setSelectedSegurado] = useState<SelfBillingTableItem | null>(null);
  const [isEditingSegurado, setIsEditingSegurado] = useState(false);
  const [isSeguradoFormValid, setIsSeguradoFormValid] = useState(false);
  const [currentFormValues, setCurrentFormValues] = useState<NewInsuredFormValues | null>(null);
  const [formResetKey, setFormResetKey] = useState(0);
  const [isSaveInFlight, setIsSaveInFlight] = useState(false);
  const [isInconsistencyModalOpen, setIsInconsistencyModalOpen] = useState(false);
  const [inconsistencies, setInconsistencies] = useState<SeguradoDataReturn[]>([]);
  const [editFormSection, setEditFormSection] = useState<PersonFormSection>('personData');
  const editSessionSnapshotRef = useRef<{
    beneficiarios: SeguradoDetailBeneficiaryItem[];
    conjugueFormValues: NewInsuredFormValues | null;
  } | null>(null);
  const { setTabBreadcrumbSuffix, setDetailBreadcrumbSuffix } = useBreadcrumb();

  const editWizardFormKey = `${selectedSegurado?.id ?? 'none'}-${formResetKey}-${isEditingSegurado ? 'edit' : 'view'}`;
  const {
    sectionControlRef: editSectionControlRef,
    onActiveSectionChange: onEditWizardSectionChange,
  } = usePersonFormWizard(editWizardFormKey);

  const onEditActiveSectionChange = useCallback((section: PersonFormSection) => {
    setEditFormSection(section);
    onEditWizardSectionChange(section);
  }, [onEditWizardSectionChange]);

  useEffect(() => {
    setEditFormSection('personData');
  }, [editWizardFormKey]);

  const sub = useSubEstipulanteInfo();
  const billing = useSelfBilling();
  const { showMessage, message, type, show, hideMessage } = useSystemMessage();

  const listControls = useVidaTableListControls({
    emptyFilters: EMPTY_SEGURADOS_PANEL_FILTERS,
    loadedRowCount: billing.list.rows.length,
    debounceMs: 400,
    resetPanelFiltersDeps: [sub.list.identSubestipulante],
  });

  const {
    debouncedSearch,
    sortBy,
    appliedPanelFilters,
    panelFilters,
    updatePanelFilter,
    resetAll: resetListControls,
    isPanelFilterActive,
  } = listControls;

  const isSubmitting = isSaveInFlight || billing.detail.isAdding || billing.detail.isSaving;
  const showBeneficiariosButton = true;
  const showConjugueButton = selectedSegurado?.tipoSeguradoCodigo?.toUpperCase() !== 'C';
  const canManageBeneficiarios = isEditingSegurado;
  const canAddConjugue = isEditingSegurado;

  const relatedPanels = useSeguradoRelatedPanels({
    formKey: editWizardFormKey,
    beneficiarios: billing.detail.beneficiarios,
    setBeneficiarios: billing.detail.setBeneficiarios,
    formSection: editFormSection,
    preserveDrafts: isEditingSegurado,
    numVidaPreviaFat: selectedSegurado?.numVidaPreviaFat,
    persistedConjugueValues: billing.detail.conjugueFormValues,
    showConjugueStep: showConjugueButton,
  });

  const {
    beneficiarioDraft,
    selectedBeneficiario,
    conjugueDraftValues,
    isBeneficiariosListActive,
    isBeneficiariosDetailActive,
    isConjuguePanelActive,
    isConjugueFormActive,
    isConjugueScreenActive,
    isConjugueLastSection,
    isConjugueFormValid,
    isWizardLastSection: isEditWizardLastSection,
    resetBeneficiariosScreen,
    resetConjugueScreen,
    resetAllRelatedPanels,
    handleOpenBeneficiarios,
    handleOpenConjugue,
    handleExitExtraPanels,
    handleAddConjugue,
    handleEditConjugue,
    handleAddBeneficiario,
    handleEditBeneficiario,
    setConjugueScreen,
    setBeneficiariosScreen,
  } = relatedPanels;

  const showSeguradoDetail = selectedSegurado !== null;
  const showSubScreen = showAddSegurado
    || showSeguradoDetail
    || sub.list.showSubestipulantePanel;

  const screenTitle = useMemo(() => {
    if (sub.list.subestipulantePanelView === 'info') {
      return 'Informações Cadastrais';
    }
    if (showAddSegurado) {
      return 'Adicionar Segurado';
    }
    if (selectedSegurado) {
      if (isConjugueFormActive) {
        return `Adicionar Cônjuge — ${selectedSegurado.nome}`;
      }
      if (isConjuguePanelActive) {
        return `Cônjuge — ${selectedSegurado.nome}`;
      }
      if (isBeneficiariosDetailActive && (beneficiarioDraft || selectedBeneficiario)) {
        return `Beneficiário — ${beneficiarioDraft?.nomeBeneficiario || selectedBeneficiario?.nomeBeneficiario || 'Novo'}`;
      }
      if (isBeneficiariosListActive) {
        return `Beneficiários — ${selectedSegurado.nome}`;
      }
      if (isEditingSegurado) {
        return `Editar Segurado — ${selectedSegurado.nome}`;
      }
      return `Visualizar Segurado — ${selectedSegurado.nome}`;
    }
    return TAB_LABEL;
  }, [
    sub.list.subestipulantePanelView,
    showAddSegurado,
    selectedSegurado,
    isEditingSegurado,
    isBeneficiariosListActive,
    isBeneficiariosDetailActive,
    selectedBeneficiario,
    beneficiarioDraft,
    isConjuguePanelActive,
    isConjugueFormActive,
  ]);

  const buildListQuery = useCallback(() => ({
    identSubestipulante: Number(sub.list.identSubestipulante),
    search: debouncedSearch,
    page: currentPage - 1,
    size: PAGE_SIZE,
  }), [sub.list.identSubestipulante, debouncedSearch, currentPage]);

  const beginEditSession = useCallback(() => {
    if (!isEditingSegurado) {
      editSessionSnapshotRef.current = {
        beneficiarios: billing.detail.beneficiarios.map((item) => ({ ...item })),
        conjugueFormValues: billing.detail.conjugueFormValues,
      };
    }
    setIsEditingSegurado(true);
  }, [
    isEditingSegurado,
    billing.detail.beneficiarios,
    billing.detail.conjugueFormValues,
  ]);

  const cancelEditSession = useCallback(() => {
    const snapshot = editSessionSnapshotRef.current;
    if (snapshot) {
      billing.detail.setBeneficiarios(snapshot.beneficiarios);
      billing.detail.setConjugueFormValues(snapshot.conjugueFormValues);
    }
    editSessionSnapshotRef.current = null;
    setCurrentFormValues(null);
    setIsEditingSegurado(false);
    resetBeneficiariosScreen();
    resetConjugueScreen();
    setFormResetKey((key) => key + 1);
  }, [
    billing.detail.setBeneficiarios,
    billing.detail.setConjugueFormValues,
    resetBeneficiariosScreen,
    resetConjugueScreen,
  ]);

  const clearEditSessionDrafts = useCallback(() => {
    editSessionSnapshotRef.current = null;
    setCurrentFormValues(null);
    resetAllRelatedPanels();
  }, [resetAllRelatedPanels]);

  const handleOpenSeguradoDetail = (row: SelfBillingTableItem) => {
    setShowAddSegurado(false);
    setIsEditingSegurado(false);
    editSessionSnapshotRef.current = null;
    setCurrentFormValues(null);
    resetAllRelatedPanels();
    setFormResetKey((key) => key + 1);
    setSelectedSegurado(row);
    void billing.openSeguradoDetail(row);
  };

  const resetInconsistencyModal = useCallback(() => {
    setIsInconsistencyModalOpen(false);
    setInconsistencies([]);
  }, []);

  useEffect(() => {
    if (!selectedSegurado || !billing.detail.selectedRow) {
      return;
    }

    if (billing.detail.selectedRow.identCertificado !== selectedSegurado.identCertificado) {
      return;
    }

    if (
      billing.detail.selectedRow.numVidaPreviaFat != null
      && billing.detail.selectedRow.numVidaPreviaFat !== selectedSegurado.numVidaPreviaFat
    ) {
      setSelectedSegurado(billing.detail.selectedRow);
    }
  }, [billing.detail.selectedRow, selectedSegurado]);

  const handleBackFromSeguradoForm = () => {
    const handled = relatedPanels.handleRelatedPanelsBack({
      clearDraftOnBack: !isEditingSegurado,
    });
    if (handled) {
      return;
    }
    if (isEditingSegurado) {
      cancelEditSession();
      return;
    }
    resetAllRelatedPanels();
    setSelectedSegurado(null);
    billing.detail.reset();
  };

  const handleOpenAddSegurado = () => {
    setSelectedSegurado(null);
    billing.detail.reset();
    resetConjugueScreen();
    setFormResetKey((key) => key + 1);
    setShowAddSegurado(true);
  };

  const handleCancelAddSegurado = () => {
    if (isSubmitting) {
      return;
    }
    setShowAddSegurado(false);
    setFormResetKey((key) => key + 1);
  };

  const handleViewSegurados = (subestipulante: ListaSubestipulanteItem) => {
    setShowAddSegurado(false);
    setSelectedSegurado(null);
    setIsEditingSegurado(false);
    billing.detail.reset();
    resetListControls();
    setCurrentPage(1);
    billing.list.reset();
    sub.resumo.close();
    sub.list.handleViewSubestipulante(subestipulante);
  };

  const handleInfoSubEstipulante = (subestipulante: ListaSubestipulanteItem) => {
    setShowAddSegurado(false);
    setSelectedSegurado(null);
    setIsEditingSegurado(false);
    billing.detail.reset();
    billing.list.reset();
    sub.resumo.close();
    sub.list.handleInfoSubEstipulante(subestipulante);
  };

  const handleBackToSubestipulantesList = () => {
    resetListControls();
    setCurrentPage(1);
    setShowAddSegurado(false);
    setSelectedSegurado(null);
    setIsEditingSegurado(false);
    billing.detail.reset();
    billing.list.reset();
    sub.resumo.close();
    sub.list.handleBackToSubestipulantesList();
  };

  const showBackToSubestipulantes = sub.list.subestipulantePanelView === 'info'
    || (!showSubScreen && sub.list.hasSearched);

  const handleBackToSubestipulantes = () => {
    if (sub.list.subestipulantePanelView === 'info') {
      sub.list.handleBackFromSubestipulantePanel();
      return;
    }
    handleBackToSubestipulantesList();
  };

  const handleBreadcrumbBackToList = useCallback(() => {
    setShowAddSegurado(false);
    setSelectedSegurado(null);
    setIsEditingSegurado(false);
    billing.detail.reset();
    billing.list.reset();
    sub.resumo.close();
    sub.list.resetSubestipulantePanel();
  }, [
    billing.detail.reset,
    billing.list.reset,
    sub.list.resetSubestipulantePanel,
    sub.resumo.close,
  ]);

  const handleBreadcrumbBackToView = useCallback(() => {
    if (isEditingSegurado) {
      cancelEditSession();
      return;
    }
    resetAllRelatedPanels();
    setFormResetKey((key) => key + 1);
  }, [cancelEditSession, isEditingSegurado, resetAllRelatedPanels]);

  const handleSaveNewSegurado = async (
    values: NewInsuredFormValues,
    conjugueValues?: NewInsuredFormValues | null,
    beneficiarios?: SeguradoDetailBeneficiaryItem[],
  ) => {
    if (!sub.list.identSubestipulante || !sub.list.selectedSubestipulante) {
      return;
    }

    setIsSaveInFlight(true);
    try {
      await billing.saveNewSegurado({
        identSubestipulante: Number(sub.list.identSubestipulante),
        contrato: sub.list.selectedSubestipulante.identContratoOut,
        formValues: values,
        conjugueFormValues: conjugueValues,
        beneficiarios,
        listQuery: buildListQuery(),
      });
      resetInconsistencyModal();
      billing.detail.reset();
      setShowAddSegurado(false);
      setFormResetKey((key) => key + 1);
      showMessage(
        conjugueValues
          ? 'Segurado e cônjuge incluídos com sucesso.'
          : 'Segurado incluído com sucesso.',
        'success',
      );
    } catch (err) {
      const parsedInconsistencies = isSeguradoInconsistencyError(err)
        ? err.inconsistencies
        : parseSeguradoInconsistenciesFromError(err);

      if (parsedInconsistencies.length > 0) {
        setInconsistencies(parsedInconsistencies);
        setIsInconsistencyModalOpen(true);
        return;
      }

      showMessage(
        resolveSelfBillingDisplayError(err, getSelfBillingErrorRoute('add')),
        'error',
      );
    } finally {
      setIsSaveInFlight(false);
    }
  };

  const handleSaveSegurado = async () => {
    const formValues = currentFormValues ?? billing.detail.formValues;
    if (!formValues || !sub.list.identSubestipulante || !sub.list.selectedSubestipulante) {
      return;
    }

    setIsSaveInFlight(true);
    try {
      if (conjugueDraftValues && !billing.detail.hasConjugue) {
        await billing.saveNewConjuge({
          identSubestipulante: Number(sub.list.identSubestipulante),
          contrato: sub.list.selectedSubestipulante.identContratoOut,
          formValues: conjugueDraftValues,
          listQuery: buildListQuery(),
        });
      }

      await billing.detail.updateSeguradoData(
        Number(sub.list.identSubestipulante),
        sub.list.selectedSubestipulante.identContratoOut,
        formValues,
      );
      resetInconsistencyModal();
      clearEditSessionDrafts();
      setIsEditingSegurado(false);
      setSelectedSegurado(null);
      billing.detail.reset();
      await billing.reloadSeguradoList(buildListQuery());
      showMessage('Segurado atualizado com sucesso.', 'success');
    } catch (err) {
      const parsedInconsistencies = isSeguradoInconsistencyError(err)
        ? err.inconsistencies
        : parseSeguradoInconsistenciesFromError(err);

      if (parsedInconsistencies.length > 0) {
        setInconsistencies(parsedInconsistencies);
        setIsInconsistencyModalOpen(true);
        return;
      }

      showMessage(
        resolveSelfBillingDisplayError(err, getSelfBillingErrorRoute('update')),
        'error',
      );
    } finally {
      setIsSaveInFlight(false);
    }
  };

  const handleEditWizardContinueOrSave = () => {
    relatedPanels.handleRelatedPanelsContinue({
      onSave: handleSaveSegurado,
      goToNextFormSection: () => editSectionControlRef.current?.goToNextSection(),
    });
  };

  const handleDeleteSelectedSegurados = async (ids: string[]) => {
    if (!sub.list.identSubestipulante || ids.length === 0) {
      return;
    }

    try {
      await billing.removeSelectedSegurados({
        identSubestipulante: Number(sub.list.identSubestipulante),
        ids,
        listQuery: buildListQuery(),
      });
      showMessage('Segurado Excluído com sucesso.', 'success');
    } catch (err) {
      showMessage(
        resolveSelfBillingDisplayError(err, getSelfBillingErrorRoute('remove')),
        'error',
      );
    }
  };

  useEffect(() => {
    const hasSubScreen = showAddSegurado
      || showSeguradoDetail
      || sub.list.showSubestipulantePanel;

    setTabBreadcrumbSuffix([
      {
        label: TAB_LABEL,
        ...(hasSubScreen ? { onClick: handleBreadcrumbBackToList } : {}),
      },
    ]);

    if (sub.list.subestipulantePanelView === 'info') {
      setDetailBreadcrumbSuffix([{ label: 'Informações Cadastrais' }]);
      return;
    }

    if (showAddSegurado) {
      setDetailBreadcrumbSuffix([{ label: 'Adicionar Segurado' }]);
      return;
    }

    if (selectedSegurado) {
      if (isConjugueFormActive) {
        setDetailBreadcrumbSuffix([
          { label: selectedSegurado.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Cônjuge', onClick: () => setConjugueScreen('panel') },
          { label: 'Adicionar Cônjuge' },
        ]);
        return;
      }
      if (isConjuguePanelActive) {
        setDetailBreadcrumbSuffix([
          { label: selectedSegurado.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Cônjuge' },
        ]);
        return;
      }
      if (isBeneficiariosDetailActive && (beneficiarioDraft || selectedBeneficiario)) {
        setDetailBreadcrumbSuffix([
          { label: selectedSegurado.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Beneficiários', onClick: () => setBeneficiariosScreen('list') },
          { label: beneficiarioDraft?.nomeBeneficiario || selectedBeneficiario?.nomeBeneficiario || 'Novo beneficiário' },
        ]);
        return;
      }
      if (isBeneficiariosListActive) {
        setDetailBreadcrumbSuffix([
          { label: selectedSegurado.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Beneficiários' },
        ]);
        return;
      }
      if (isEditingSegurado) {
        setDetailBreadcrumbSuffix([
          { label: selectedSegurado.nome, onClick: handleBreadcrumbBackToView },
          { label: 'Editar Segurado' },
        ]);
        return;
      }
      setDetailBreadcrumbSuffix([{ label: selectedSegurado.nome }]);
      return;
    }

    setDetailBreadcrumbSuffix([]);
  }, [
    sub.list.subestipulantePanelView,
    sub.list.showSubestipulantePanel,
    showAddSegurado,
    showSeguradoDetail,
    selectedSegurado,
    isEditingSegurado,
    isBeneficiariosListActive,
    isBeneficiariosDetailActive,
    selectedBeneficiario,
    beneficiarioDraft,
    isConjuguePanelActive,
    isConjugueFormActive,
    setTabBreadcrumbSuffix,
    setDetailBreadcrumbSuffix,
    handleBreadcrumbBackToList,
    handleBreadcrumbBackToView,
  ]);

  useEffect(() => () => {
    setDetailBreadcrumbSuffix([]);
  }, [setDetailBreadcrumbSuffix]);

  useEffect(() => {
    if (billing.detail.error) {
      showMessage(
        resolveSelfBillingDisplayError(billing.detail.error, getSelfBillingErrorRoute('detail')),
        'error',
      );
      return;
    }

    if (billing.list.error) {
      showMessage(
        resolveSelfBillingDisplayError(billing.list.error, getSelfBillingErrorRoute('list')),
        'error',
      );
      return;
    }

    hideMessage();
  }, [billing.detail.error, billing.list.error, showMessage, hideMessage]);

  useEffect(() => {
    resetInconsistencyModal();
  }, [formResetKey, resetInconsistencyModal]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, sortBy, sub.list.identSubestipulante]);

  useEffect(() => {
    if (!sub.list.identSubestipulante) {
      billing.list.reset();
      return;
    }

    billing.syncSeguradoList(sub.list.hasSearched, buildListQuery());
  }, [
    sub.list.hasSearched,
    sub.list.identSubestipulante,
    buildListQuery,
    billing.syncSeguradoList,
    billing.list.reset,
  ]);

  const filteredSeguradosRows = useMemo(
    () => filterSeguradoTableRows(billing.list.rows, appliedPanelFilters),
    [billing.list.rows, appliedPanelFilters],
  );

  const sortedSeguradosRows = useMemo(
    () => sortSeguradoTableRows(filteredSeguradosRows, sortBy),
    [filteredSeguradosRows, sortBy],
  );

  const totalItems = billing.list.totalElements;
  const totalPages = Math.max(1, billing.list.totalPages);
  const tableCurrentPage = isPanelFilterActive ? 1 : currentPage;
  const tableTotalPages = isPanelFilterActive ? 1 : totalPages;
  const tableTotalItems = isPanelFilterActive ? sortedSeguradosRows.length : totalItems;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  return (
    <div
      className="bsds-width-100 bsds-mt-12 bsds-p-8"
      role="main"
      aria-label="Auto faturamento de segurados"
      id={mainContentId}
    >
      {show && (
        <div
          id={messageId}
          className="bsds-mb-16"
          role={type === 'error' ? 'alert' : 'status'}
          aria-live={type === 'error' ? 'assertive' : 'polite'}
          aria-atomic="true"
        >
          <SystemMessage
            message={message}
            type={type}
            show={show}
          />
        </div>
      )}

      {showBackToSubestipulantes && (
        <button
          type="button"
          className="bsds-display-flex bsds-align-items-center cursor-pointer text-gray-700 bsds-mb-16 border-0 bg-transparent p-0 mb-8"
          onClick={handleBackToSubestipulantes}
          aria-label="Voltar para listagem de subestipulantes"
        >
          <BsdsIcon
            icon={iconSetaDuplaEsquerda}
            className="w-4 h-4 text-gray-700 shrink-0 bsds-mr-8"
            aria-hidden="true"
          />
          Voltar
        </button>
      )}

      <div className="bsds-display-flex flex-col md:flex-row bsds-justify-content-between md:bsds-align-items-center gap-3 bsds-mb-24">
        <div className="bsds-display-flex bsds-justify-content-start bsds-align-items-center">
          <div className="bsds-display-flex bsds-align-items-center">
            <div className="w-[2.5px] h-8  bg-red-600 bsds-mr-8" aria-hidden="true" />
          </div>
          <h6 className="bsds-ml-8 font-normal text-lg">
            {showSubScreen
              ? [
                  screenTitle,
                  sub.list.selectedSubestipulante?.nomeSubestipulanteOut?.trim(),
                ].filter(Boolean).join(' - ')
              : TAB_LABEL}
          </h6>
        </div>
        {!showSubScreen && sub.list.hasSearched && (
          <div className="bsds-display-flex md:bsds-justify-content-end bsds-align-items-center min-h-10">
            <Button
              type="button"
              color="primary"
              fill="solid"
              icon={iconNavAdicionar1}
              iconPosition="start"
              onClick={handleOpenAddSegurado}
              aria-label="Nova inclusão de segurado"
            >
              Nova Inclusão
            </Button>
          </div>
        )}
      </div>

      <Visibility isActive={showAddSegurado} animated animationDuration={300}>
        <NewSeguradoForm
          formKey={formResetKey}
          isSaving={isSubmitting}
          onSave={handleSaveNewSegurado}
          onCancel={handleCancelAddSegurado}
        />
      </Visibility>

      <Visibility isActive={showSeguradoDetail} animated animationDuration={300}>
        <div className="bsds-mt-24" role="region" aria-label="Dados do segurado" aria-busy={billing.detail.isLoadingDetail || isSubmitting}>
          {billing.detail.formValues && (
            <SeguradoFormSection
              key={`${selectedSegurado?.id}-${formResetKey}`}
              initialValues={billing.detail.formValues}
              readOnly={!isEditingSegurado || isSubmitting}
              showDataCadastro
              cpfReadOnly
              dataCadastroReadOnly
              onValidityChange={setIsSeguradoFormValid}
              onValuesChange={setCurrentFormValues}
              showBeneficiariosButton={showBeneficiariosButton}
              isBeneficiariosActive={isBeneficiariosListActive || isBeneficiariosDetailActive}
              onBeneficiariosClick={handleOpenBeneficiarios}
              showConjugueButton={showConjugueButton}
              isConjugueActive={isConjugueScreenActive}
              onConjugueClick={handleOpenConjugue}
              onExitExtraPanels={handleExitExtraPanels}
              sectionControlRef={editSectionControlRef}
              onActiveSectionChange={onEditActiveSectionChange}
            />
          )}

          <SeguradoRelatedPanelsSection
            formKey={editWizardFormKey}
            isSaving={isSubmitting}
            canManageBeneficiarios={canManageBeneficiarios}
            canAddConjugue={canAddConjugue}
            isBeneficiariosListActive={isBeneficiariosListActive}
            isBeneficiariosDetailActive={isBeneficiariosDetailActive}
            isConjugueScreenActive={isConjugueScreenActive}
            isConjugueFormActive={isConjugueFormActive}
            beneficiariosRows={relatedPanels.beneficiariosRows}
            selectedBeneficiario={selectedBeneficiario}
            beneficiarioDraft={beneficiarioDraft}
            beneficiarioFormInitialValues={relatedPanels.beneficiarioFormInitialValues}
            conjugueDisplayValues={relatedPanels.conjugueDisplayValues}
            conjugueSectionControlRef={relatedPanels.conjugueSectionControlRef}
            onConjugueActiveSectionChange={relatedPanels.onConjugueActiveSectionChange}
            onEditBeneficiario={handleEditBeneficiario}
            onAddBeneficiario={handleAddBeneficiario}
            onBeneficiarioValidityChange={relatedPanels.setIsBeneficiarioValid}
            onBeneficiarioValuesChange={relatedPanels.setBeneficiarioDraft}
            onAddConjugue={handleAddConjugue}
            onEditConjugue={handleEditConjugue}
            onConjugueValidityChange={relatedPanels.setIsConjugueFormValid}
            onConjugueValuesChange={relatedPanels.setConjugueDraftValues}
          />

          <div className="bsds-display-flex bsds-pt-16 flex-col sm:flex-row gap-4 mt-24">
            {isEditingSegurado && (
              <PersonFormActionButtons
                isLastSection={isConjugueFormActive ? isConjugueLastSection : isEditWizardLastSection}
                isSaving={isSubmitting}
                canSave={isConjugueFormActive ? isConjugueFormValid : isSeguradoFormValid}
                onPrimaryClick={handleEditWizardContinueOrSave}
                onCancel={cancelEditSession}
                saveLabel={isConjugueFormActive ? 'Confirmar Cônjuge' : 'Salvar'}
                saveAriaLabel={
                  isConjugueFormActive
                    ? 'Confirmar dados do cônjuge'
                    : `Salvar alteracoes de ${selectedSegurado?.nome}`
                }
                continueAriaLabel={
                  isConjugueFormActive
                    ? 'Continuar cadastro do cônjuge'
                    : `Continuar edicao de ${selectedSegurado?.nome}`
                }
                cancelAriaLabel={`Cancelar edicao de ${selectedSegurado?.nome}`}
              />
            )}
            {!isEditingSegurado && isConjuguePanelActive && (
              <>
                <Button
                  type="button"
                  color="primary"
                  fill="solid"
                  className="bsds-width-100 bsds-sm-width-50 order-1 sm:order-2"
                  loading={billing.detail.isLoadingDetail}
                  onClick={() => beginEditSession()}
                  disabled={isSubmitting}
                  aria-label={`Editar cadastro de ${selectedSegurado?.nome}`}
                  aria-busy={billing.detail.isLoadingDetail}
                >
                  Editar
                </Button>
                <Button
                  type="button"
                  onClick={handleBackFromSeguradoForm}
                  color="secondary"
                  fill="outline"
                  className="bsds-width-100 bsds-sm-width-50 order-2 sm:order-1"
                  aria-label="Voltar para dados do segurado"
                >
                  Voltar
                </Button>
              </>
            )}
            {!isEditingSegurado && !isConjugueScreenActive && !isBeneficiariosListActive && !isBeneficiariosDetailActive && (
              <>
                <Button
                  type="button"
                  color="primary"
                  fill="solid"
                  className="bsds-width-100 bsds-sm-width-50 order-1 sm:order-2"
                  loading={billing.detail.isLoadingDetail}
                  onClick={() => {
                    beginEditSession();
                  }}
                  disabled={isSubmitting}
                  aria-label={`Editar cadastro de ${selectedSegurado?.nome}`}
                  aria-busy={billing.detail.isLoadingDetail}
                >
                  Editar
                </Button>
                <Button
                  type="button"
                  onClick={handleBackFromSeguradoForm}
                  color="secondary"
                  fill="outline"
                  className="bsds-width-100 bsds-sm-width-50 order-2 sm:order-1"
                  aria-label="Voltar para lista de segurados"
                >
                  Voltar
                </Button>
              </>
            )}
            {!isEditingSegurado && !isConjugueScreenActive && (isBeneficiariosListActive || isBeneficiariosDetailActive) && (
              <>
                <Button
                  type="button"
                  color="primary"
                  fill="solid"
                  className="bsds-width-100 bsds-sm-width-50 order-1 sm:order-2"
                  loading={billing.detail.isLoadingDetail}
                  onClick={() => beginEditSession()}
                  disabled={isSubmitting}
                  aria-label={`Editar cadastro de ${selectedSegurado?.nome}`}
                  aria-busy={billing.detail.isLoadingDetail}
                >
                  Editar
                </Button>
                <Button
                  type="button"
                  onClick={handleBackFromSeguradoForm}
                  color="secondary"
                  fill="outline"
                  className="bsds-width-100 bsds-sm-width-50 order-2 sm:order-1"
                  aria-label="Voltar para dados do segurado"
                >
                  Voltar
                </Button>
              </>
            )}
          </div>
        </div>
      </Visibility>

      <Visibility isActive={sub.list.subestipulantePanelView === 'info'} animated animationDuration={300}>
        <SubEstipulanteInfoSection
          subestipulante={sub.list.selectedSubestipulante}
          info={sub.info}
        />
      </Visibility>

      <Visibility isActive={!showSubScreen} animated animationDuration={300}>
        <SubestipulantesListSection
          hasSearched={sub.list.hasSearched}
          selectedSubestipulante={sub.list.selectedSubestipulante}
          subestipulantesList={sub.list.subestipulantesList}
          numeroApolice={sub.list.numeroApoliceSubestipulantes}
          subestipulanteResumo={sub.resumo}
          viewAction="segurados"
          onViewSubestipulante={handleViewSegurados}
          onInfoSubEstipulante={handleInfoSubEstipulante}
        />

        {sub.list.hasSearched && (
          <>
            <VidaTableListToolbar
              groupAriaLabel="Busca na lista e ordenacao de segurados"
              searchPlaceholder="Filtre por matricula, CPF, nome ou status"
              searchAriaLabel="Filtre por matricula, CPF, nome ou status"
              sortId="sort-self-billing"
              sortOptions={SEGURADOS_SORT_OPTIONS}
              filtersPanelAriaLabel="Configurações de filtros de segurados"
              entityLabel="segurados"
              controls={listControls}
            >
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Matrícula"
                    value={panelFilters.matricula}
                    onInput={(e) => updatePanelFilter('matricula', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Tipo Segurado"
                    value={panelFilters.tipoSegurado}
                    onInput={(e) => updatePanelFilter('tipoSegurado', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Nome"
                    value={panelFilters.nome}
                    onInput={(e) => updatePanelFilter('nome', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Tipo Documento"
                    value={panelFilters.tipoDocumento}
                    onInput={(e) => updatePanelFilter('tipoDocumento', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
              <div className="bsds-display-flex flex-col sm:flex-row gap-4">
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="CPF"
                    value={panelFilters.cpf}
                    onInput={(e) => updatePanelFilter('cpf', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
                <div className="bsds-width-100 sm:bsds-flex-1">
                  <BsdsInput
                    type="text"
                    clearInput={false}
                    label="Certificado"
                    value={panelFilters.certificado}
                    onInput={(e) => updatePanelFilter('certificado', extractBsdsInputValue(e))}
                    platform="web"
                  />
                </div>
              </div>
            </VidaTableListToolbar>

            <div
              className="bsds-mt-16"
              role="region"
              aria-label="Lista de segurados"
              aria-busy={billing.list.isLoading}
            >
              <SelfBillingTable
                rows={sortedSeguradosRows}
                currentPage={tableCurrentPage}
                totalPages={tableTotalPages}
                totalItems={tableTotalItems}
                onPageChange={handlePageChange}
                onDeleteSelected={(ids) => void handleDeleteSelectedSegurados(ids)}
                isDeleting={billing.list.isDeleting}
                onRowClick={handleOpenSeguradoDetail}
                emptyMessage={
                  billing.list.isLoading
                    ? 'Carregando lista de Segurados...'
                    : 'Nenhum Segurado encontrado para a pesquisa informada'
                }
              />
            </div>
          </>
        )}
      </Visibility>

      <SeguradoInconsistencyModal
        isOpen={isInconsistencyModalOpen}
        inconsistencies={inconsistencies}
        onClose={resetInconsistencyModal}
      />
    </div>
  );
};

export default SelfBillingTab;
